import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { OneTimeModal as OneTimeModalType } from '@backrs_ui/grow-ui/src/redux/oneTimeModal/oneTimeModal.types';

import { Box, Dialog, Fade, SxProps, Theme, Button } from '@mui/material';

import { OneTimeModalDots } from './OneTimeModalDots';
import { OneTimeModalSlide } from './OneTimeModalSlide';

const BUTTON_STRINGS = appStrings.common.button;

const TRANSITION_DURATION = 200;

type OneTimeModalProps = Readonly<{
  open: boolean;
  modal: OneTimeModalType;
  onClose: () => void;
}>;

/**
 * Preloads images and tracks which ones have finished loading
 */
const useImagePreloader = (imageUrls: string[]) => {
  const [loadedUrls, setLoadedUrls] = useState<Set<string>>(new Set());
  const initiatedUrls = useRef<Set<string>>(new Set());

  useEffect(() => {
    imageUrls.forEach((url) => {
      if (!url || initiatedUrls.current.has(url)) return;

      initiatedUrls.current.add(url);
      const img = new Image();

      img.onload = () => {
        setLoadedUrls((prev) => new Set(prev).add(url));
      };
      img.onerror = () => {
        // Mark as "loaded" to prevent skeleton from showing indefinitely
        setLoadedUrls((prev) => new Set(prev).add(url));
      };
      img.src = url;
    });
  }, [imageUrls]);

  return loadedUrls;
};

export const OneTimeModal = ({ open, modal, onClose }: OneTimeModalProps) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [contentVisible, setContentVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(open);

  const sortedSlides = useMemo(
    () => [...modal.slides].sort((a, b) => a.order - b.order),
    [modal.slides],
  );

  // Extract all image URLs for preloading
  const imageUrls = useMemo(
    () => sortedSlides.map((slide) => slide.media?.media_url).filter((url): url is string => !!url),
    [sortedSlides],
  );

  const loadedImages = useImagePreloader(imageUrls);

  const totalSlides = sortedSlides.length;
  const currentSlide = sortedSlides[currentSlideIndex];
  const isLastSlide = currentSlideIndex === totalSlides - 1;

  // Check if current slide's image is loaded (or has no image)
  const currentImageUrl = currentSlide?.media?.media_url;
  const isCurrentImageReady = !currentImageUrl || loadedImages.has(currentImageUrl);

  const navigateToSlide = useCallback(
    (targetIndex: number) => {
      if (isTransitioning || targetIndex === currentSlideIndex) return;

      setIsTransitioning(true);
      setContentVisible(false);

      // After fade out, change slide and fade in
      setTimeout(() => {
        setCurrentSlideIndex(targetIndex);
        setContentVisible(true);
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    },
    [isTransitioning, currentSlideIndex],
  );

  const handleNext = useCallback(() => {
    if (isLastSlide) {
      setIsModalOpen(false);
      setTimeout(onClose, TRANSITION_DURATION);
    } else {
      navigateToSlide(currentSlideIndex + 1);
    }
  }, [isLastSlide, onClose, navigateToSlide, currentSlideIndex]);

  const handleSkip = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(onClose, TRANSITION_DURATION);
  }, [onClose]);

  const handleDotClick = useCallback(
    (index: number) => {
      navigateToSlide(index);
    },
    [navigateToSlide],
  );

  if (!currentSlide) {
    return null;
  }

  return (
    <Dialog
      open={isModalOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          setIsModalOpen(false);
          setTimeout(onClose, TRANSITION_DURATION);
        }
      }}
      sx={dialogSx}
      maxWidth="sm"
      fullWidth
      aria-labelledby="one-time-modal-title"
    >
      <Box sx={containerSx}>
        <Box sx={slideRegionSx}>
          <Fade in={contentVisible} timeout={TRANSITION_DURATION}>
            <Box sx={fadeChildSx}>
              <OneTimeModalSlide slide={currentSlide} isImageLoaded={isCurrentImageReady} />
            </Box>
          </Fade>
        </Box>

        <Box sx={footerSx}>
          <OneTimeModalDots
            totalSlides={totalSlides}
            currentIndex={currentSlideIndex}
            onDotClick={handleDotClick}
          />
          <Box sx={actionContainerSx}>
            <Button
              id="one-time-modal-skip-button"
              hierarchy="Secondary gray"
              size="md"
              onClick={handleSkip}
              fullWidth
              disabled={isTransitioning}
            >
              {BUTTON_STRINGS.skip}
            </Button>

            <Button
              id="one-time-modal-next-button"
              hierarchy="Primary"
              size="md"
              onClick={handleNext}
              fullWidth
              disabled={isTransitioning}
            >
              {isLastSlide ? BUTTON_STRINGS.letsGo : BUTTON_STRINGS.next}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

const dialogSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiDialog-paper': {
    borderRadius: backrs.radius['radius-xl'],
    boxShadow: backrs.shadows['shadow-xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
});

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
};

const slideRegionSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const fadeChildSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const actionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
});

const footerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  gap: backrs.spacing['spacing-xl'],
  padding: backrs.spacing['spacing-3xl'],
  paddingTop: backrs.spacing['spacing-xl'],
});
